import {Fragment, useEffect, useState} from "react";
import {format} from "date-fns";
import {BreadCrumb, Table, CenteredModal, FormInput} from "~/components";
import {SkeletonTableLoading} from '~/components';
import ServicePlanAPI from "~/api/servicePlanApi";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import {useFieldArray, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {LucideSquarePlus, Trash, Plus} from "lucide-react";

const breadCrumbItem = [
    { text: "AI Chatbot Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Service Manager", to: "", isActive: true },
]
function ServicePlanManager() {
    const [reloadTable, setReloadTable] = useState(false);
    const [servicePlan, setServicePlan] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showPlanDescriptionModal, setShowPlanDescriptionModal] = useState(false);

    const handleNewPlanDescription = () => {
        ServicePlanAPI.getPlanDescription()
            .then((res) => {
                resetPD({ PlanDescriptions: res.data });
                setShowPlanDescriptionModal(true);
            })
            .catch(() => {
            })
    }

    const tableConfig = {
        columns: [
            {
                name: 'ID',
                width: '40px',
                className: 'fw-bold',
                key: 'ServicePlanId'
            },
            {
                name: 'SERVICE TYPE',
                width: '150px',
                key: 'ServiceType'
            },
            {
                name: 'DESCRIPTION',
                width: '250px',
                key: 'Description'
            },
            {
                name: 'SERVICE NAME',
                width: '150px',
                key: 'Name'
            },
            {
                name: 'DISCOUNT',
                width: '100px',
                key: 'Discount',
                template : (row) => {
                    return (
                        <span>{row.Discount}%</span>
                    )
                }
            },
            {
                name: 'PRICE',
                width: '160px',
                key: "Price",
                template : (row) => {
                    return (
                        <span>${row.Price}</span>
                    )
                }
            },
            {
                name: 'DURATION VALUE',
                width: '160px',
                key: "DurationValue"
            },
            {
                name: 'DURATION UNIT',
                width: '160px',
                key: "DurationUnit"
            },
            {
                name: 'USER LIMITED',
                width: '160px',
                key: "UserLimit"
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                headerClassName: 'text-center',
                className: 'text-center',
                template: (row) => {
                    return (
                        <span><FontAwesomeIcon onClick={()=> handleEditServiceManager(row.ServicePlanId)} icon={faEdit} className="text-primary cursor-pointer"/></span>
                    )
                }
            }
        ],
        tableKey: "TableServiceManager",
        extraButtons: [
            <button
                key="newPlan"
                className="btn btn-bt-primary ms-4"
                type="button"
                onClick={handleNewPlanDescription}
            >
                Update Plan Description <LucideSquarePlus size={15}/>
            </button>
        ]
    }

    useEffect(() => {
        setLoading(true);
        ServicePlanAPI.getAll()
            .then((res) => {
                setServicePlan(res.data);
            })
            .catch(() => {
            })
            .finally(() => {
                setLoading(false);
            })
    }, [reloadTable]);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        getValues,
        formState: { errors }
    } = useForm();

    const {
        register : registerPD,
        handleSubmit: handleSubmitPD,
        reset: resetPD,
        setValue: setValuePD,
        getValues: getValuePD,
        control,
        formState: { errors: errorsPD }
    } = useForm();

    const { fields, append, remove } = useFieldArray({
        control,
        name: "PlanDescriptions"
    });

    const handleEditServiceManager = (servicePlanId) => {
        ServicePlanAPI.getById(servicePlanId)
            .then((res) => {
                reset(res.data);
                setShowModal(true);
            })
            .catch(() => {
            })
    }

    const onSubmitFrom = (data) => {
        ServicePlanAPI.update(data)
            .then((res) => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your service plan has been updated.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your service plan.</span>)
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while updating your service plan.</span>)
            })
            .finally(() => {
                setShowModal(false);
                setReloadTable(prev => !prev);
            })
    }

    const onSubmitFormPlanDescription = (data) => {
        ServicePlanAPI.updPlanDescription(data)
            .then((res) => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your plan description has been updated.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your plan description.</span>)
            })
            .catch((err) => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while updating your plan description.</span>)
            })
            .finally(() => {
                setShowPlanDescriptionModal(false);
            })
    }

    const handleAddDescription = () => {
        const newItem = {
            PlanDescriptionId: 0,
            Description: "",
            IsActive: false,
            IsDeleted: false,
            ModifiedBy: "",
            ModifiedDate: new Date(),
            RowNumber: 0,
            RequestedUserId: "0"
        };
        append(newItem);
    };

    return(
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5">Service Manager</h2>
            {loading ? (
                <SkeletonTableLoading columns={tableConfig.columns} rows={10} />
            ) : (
                <Table tableConfig={{...tableConfig, data : servicePlan}}/>
            )}
            <CenteredModal
                show={showModal}
                onClose={() => setShowModal(false)}
                title="Edit Service Plan"
                formId="formEditServiceManager"
                modalHeight="600px"
            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formEditServiceManager">
                    <div>
                        <div className="mb-3 nav nav-underline" role="tablist">
                            <div className="nav-item">
                                <a
                                    className="nav-link active"
                                    id="tab-main-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-main"
                                    role="tab"
                                    aria-controls="tab-main"
                                    aria-selected="true"
                                >
                                    Main
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-mapping-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-mapping"
                                    role="tab"
                                    aria-controls="tab-mapping"
                                    aria-selected="false"
                                >
                                    Assign Value
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-description-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-description"
                                    role="tab"
                                    aria-controls="tab-description"
                                    aria-selected="false"
                                >
                                    Plan Description
                                </a>
                            </div>
                        </div>
                        <div className="tab-content">
                            <div
                                className="tab-pane fade show active"
                                id="tab-main"
                                role="tabpanel"
                                aria-labelledby="tab-main-tab"
                            >
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="ServiceType"
                                               errors={errors}
                                               required={true}
                                               disabled={true}
                                               maxLengthText=""
                                               label="Assign Service Type"
                                               validation={{
                                                   ...register("ServiceType", {
                                                       required: "Service type is required."
                                                   })
                                               }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="Name"
                                               errors={errors}
                                               required={true}
                                               label="Assign Service Name"
                                               maxLengthText="max length 255 character"
                                               validation={{
                                                   ...register("Name", {
                                                       required: "Service name is required."
                                                   })
                                               }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name="Description"
                                               errors={errors}
                                               textarea={true}
                                               required={true}
                                               label="Assign Description"
                                               validation={{
                                                   ...register("Description", {
                                                       required: "Description is required."
                                                   })
                                               }}
                                    />
                                </div>
                            </div>

                            <div
                                className="tab-pane fade"
                                id="tab-mapping"
                                role="tabpanel"
                                aria-labelledby="tab-mapping-tab"
                            >
                                <div className="mb-3">
                                    <div className="row">
                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "Discount"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       label="Assign Discount"
                                                       validation={{...register('Discount', {
                                                               required: 'Discount is required.',
                                                               pattern: {
                                                                   value: /^[0-9]+$/,
                                                                   message: 'Only numbers are allowed.'
                                                               },
                                                               validate: value => {
                                                                   const num = parseInt(value, 10);
                                                                   if (isNaN(num)) return 'Invalid number.';
                                                                   if (num < 0) return 'Must be at least 0.';
                                                                   if (num > 100) return 'Must be no more than 100.';
                                                                   return true;
                                                               }
                                                           })}
                                                       }
                                            />
                                            <span className="position-absolute input-unit fw-bold">%</span>
                                        </div>
                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "Price"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       label="Assign Price"
                                                       validation={{...register('Price', {
                                                               required: 'Price is required.',
                                                               pattern: {
                                                                   value: /^[0-9]+$/,
                                                                   message: 'Only numbers are allowed.'
                                                               },
                                                               validate: value => {
                                                                   const num = parseInt(value, 10);
                                                                   if (isNaN(num)) return 'Invalid number.';
                                                                   if (num < 0) return 'Must be at least 0.';
                                                                   if (num > 99999) return 'Must be no more than 99999.';
                                                                   return true;
                                                               }
                                                           })}
                                                       }
                                            />
                                            <span className="position-absolute input-unit fw-bold">$</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="row">
                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "DurationValue"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       label="Assign Duration Value"
                                                       validation={{...register('DurationValue', {
                                                               required: 'Duration Value is required.',
                                                               pattern: {
                                                                   value: /^[0-9]+$/,
                                                                   message: 'Only numbers are allowed.'
                                                               },
                                                               validate: value => {
                                                                   const num = parseInt(value, 10);
                                                                   if (isNaN(num)) return 'Invalid number.';
                                                                   if (num < 0) return 'Must be at least 0.';
                                                                   if (num > 99999) return 'Must be no more than 99999.';
                                                                   return true;
                                                               }
                                                           })}
                                                       }
                                            />
                                        </div>

                                        <div className="col-sm-6 col-12 position-relative">
                                            <FormInput showHeaderLabel={true}
                                                       name = "UserLimit"
                                                       errors={errors}
                                                       required= {true}
                                                       maxLengthText=""
                                                       disabled={getValues("TypeId") === 1}
                                                       label="Assign User Limit"
                                                       validation={{...register('UserLimit', {
                                                               required: 'User Limit is required.',
                                                               pattern: {
                                                                   value: /^[0-9]+$/,
                                                                   message: 'Only numbers are allowed.'
                                                               },
                                                               validate: value => {
                                                                   const num = parseInt(value, 10);
                                                                   if (isNaN(num)) return 'Invalid number.';
                                                                   if (num < 0) return 'Must be at least 0.';
                                                                   if (num > 99999) return 'Must be no more than 99999.';
                                                                   return true;
                                                               }
                                                           })}
                                                       }
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="mb-2"><span className="fs-8-1 fw-semibold">Duration Unit</span></div>
                                    <div className="form-check form-check-inline">
                                        <input id="rad1"
                                               className="form-check-input"
                                               type="radio"
                                               value = {"Hour"}
                                               {...register("DurationUnit")}
                                        />
                                        <label title="" htmlFor="rad1" className="form-check-label">Hour</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input id="rad2"
                                               className="form-check-input"
                                               type="radio"
                                               value = {"Day"}
                                               {...register("DurationUnit")}
                                        />
                                        <label title="" htmlFor="rad2" className="form-check-label">Day</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input id="rad3"
                                               className="form-check-input"
                                               type="radio"
                                               value = {"Week"}
                                               {...register("DurationUnit")}
                                        />
                                        <label title="" htmlFor="rad3" className="form-check-label">Week</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input id="rad4"
                                               className="form-check-input"
                                               type="radio"
                                               value = {"Month"}
                                               {...register("DurationUnit")}
                                        />
                                        <label title="" htmlFor="rad4" className="form-check-label">Month</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input id="rad5"
                                               className="form-check-input"
                                               type="radio"
                                               value = {"Year"}
                                               {...register("DurationUnit")}
                                        />
                                        <label title="" htmlFor="rad5" className="form-check-label">Year</label>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="tab-pane fade"
                                id="tab-description"
                                role="tabpanel"
                                aria-labelledby="tab-description-tab"
                            >
                                {
                                    getValues("PlanDescriptionMappings") &&
                                    getValues("PlanDescriptionMappings").map((item, index) => (
                                        <div className="form-check" key={index}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                {...register(`PlanDescriptionMappings[${index}].IsApply`)}
                                                id={`planDescriptionMappings-${index}`}
                                            />
                                            <label className="form-check-label" htmlFor={`planDescriptionMappings-${index}`}>
                                                {item.Description}
                                            </label>
                                        </div>

                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </form>
            </CenteredModal>
            <CenteredModal
                show={showPlanDescriptionModal}
                onClose={() => setShowPlanDescriptionModal(false)}
                title="Update Plan Description"
                formId="formUpdatePlanDescription"
            >
                <form onSubmit={handleSubmitPD(onSubmitFormPlanDescription)} id="formUpdatePlanDescription">
                    {
                        fields.map((item, index) => {
                            return (
                                <div className="mb-3" key={index}>
                                    <div className="row">
                                        <div className="col-sm-11 col-12">
                                            <FormInput
                                                label="Assign Description"
                                                name={`PlanDescriptions[${index}].Description`}
                                                errors={errorsPD}
                                                required={true}
                                                defaultValue={item.Description}
                                                maxLengthText = "max length 250 character"
                                                showHeaderLabel={false}
                                                validation={{...registerPD(`PlanDescriptions[${index}].Description`, {
                                                        required: "Description is required.",
                                                    })}}
                                            />
                                        </div>
                                        <div className="col-sm-1 col-12">
                                            <Trash size={15} onClick={() => remove(index)} className="mt-3 text-danger cursor-pointer" />
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    }
                    <div className="text-center">
                        <button
                            className="btn btn-bt-primary ms-4"
                            type="button"
                            onClick={handleAddDescription}
                        >
                            Add New Description <Plus size={15}/>
                        </button>
                    </div>
                </form>
            </CenteredModal>
        </Fragment>
    )
}
export default ServicePlanManager;